import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Clock, FileSpreadsheet, Inbox, AlertCircle } from 'lucide-react';

function getPains(t: (key: string) => string) {
  return [
    {
      icon: Clock,
      title: t('admin:landing.pain.items.chasing.title'),
      description: t('admin:landing.pain.items.chasing.description'),
      gradient: "from-rose-500 to-pink-500",
    },
    {
      icon: FileSpreadsheet,
      title: t('admin:landing.pain.items.diyProspecting.title'),
      description: t('admin:landing.pain.items.diyProspecting.description'),
      gradient: "from-blue-500 to-cyan-500",
    },
    {
      icon: Inbox,
      title: t('admin:landing.pain.items.ignoredEmails.title'),
      description: t('admin:landing.pain.items.ignoredEmails.description'),
      gradient: "from-amber-500 to-orange-500",
    },
    {
      icon: AlertCircle,
      title: t('admin:landing.pain.items.looseFollowUp.title'),
      description: t('admin:landing.pain.items.looseFollowUp.description'),
      gradient: "from-purple-500 to-fuchsia-500",
    },
  ];
}

export default function PainSection() {
  const { t } = useTranslation(['admin']);
  const pains = getPains(t);
  return (
    <section id="pains" className="relative py-28 bg-[#0a0a0a]">
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
      <div className="max-w-6xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-14"
        >
          <span className="text-xs uppercase tracking-[0.2em] text-rose-400 mb-4 block">
            {t('admin:landing.pain.label')}
          </span>
          <h2 className="text-3xl md:text-5xl font-bold text-white">
            {t('admin:landing.pain.heading')}
          </h2>
          <p className="text-white/50 mt-4 max-w-2xl mx-auto">
            {t('admin:landing.pain.subheading')}
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-4 md:gap-6">
          {pains.map((p, i) => (
            <motion.div
              key={p.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.05 }}
              className="p-6 md:p-7 rounded-2xl bg-white/[0.02] border border-white/[0.06] hover:bg-white/[0.04] hover:border-white/[0.1] transition-colors"
            >
              <div className="flex items-start gap-4">
                <div className={`shrink-0 w-10 h-10 rounded-xl bg-gradient-to-br ${p.gradient} flex items-center justify-center`}>
                  <p.icon className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-white font-semibold text-lg mb-1.5">{p.title}</h3>
                  <p className="text-white/60 text-sm leading-relaxed">{p.description}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        <div className="text-center mt-10">
          <a
            href="#services"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-br from-rose-500 to-fuchsia-600 text-white text-sm border border-white/10 hover:opacity-95 transition-opacity"
          >
            {t('admin:landing.pain.cta')}
          </a>
        </div>
      </div>
    </section>
  );
}